import React, { useEffect, useRef, useState } from "react";
import { Button, Input } from "antd";
import { ArrowLeft, Mail, RefreshCw } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { verifyEmailOtp, resendEmailOtp } from "../store/slices/authSlice";
import { toast } from "react-toastify";

const EmailVerificationPage = ({ email: emailProp = "", onVerificationSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // prefer location.state.email, fallback to prop
  const emailFromState = location.state?.email || emailProp || "";

  const [otpArr, setOtpArr] = useState(Array(6).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const inputsRef = useRef([]);
  const resendIntervalRef = useRef(null);
  const dispatch = useDispatch();



  useEffect(() => {
    return () => {
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }
    };
  }, []);

  // join otp array into string
  const otp = otpArr.join("");

  const handleChange = (value, idx) => {
    // Only keep last character (in case user pastes)
    const char = value.slice(-1).replace(/\s+/g, "");
    if (char && !/^[0-9]$/.test(char)) {
      // allow only digits
      return;
    }

    const next = [...otpArr];
    next[idx] = char;
    setOtpArr(next);

    if (char && idx < 5) {
      // focus next input
      const nextInput = inputsRef.current[idx + 1];
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (otpArr[idx] === "") {
        // move to previous if current empty
        if (idx > 0) {
          const prevInput = inputsRef.current[idx - 1];
          if (prevInput) prevInput.focus();
          const next = [...otpArr];
          next[idx - 1] = "";
          setOtpArr(next);
        }
      } else {
        // clear current value
        const next = [...otpArr];
        next[idx] = "";
        setOtpArr(next);
      }
    }

    // allow arrow navigation
    if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowRight" && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }

    // If user pastes (Ctrl+V) handled in onPaste
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    const digits = paste.replace(/\D/g, "").slice(0, 6).split("");
    if (digits.length === 0) return;
    const next = [...otpArr];
    for (let i = 0; i < 6; i++) {
      next[i] = digits[i] || "";
    }
    setOtpArr(next);
    // focus the next empty index (or last)
    const firstEmpty = next.findIndex((d) => d === "");
    const focusIndex = firstEmpty === -1 ? 5 : firstEmpty;
    setTimeout(() => inputsRef.current[focusIndex]?.focus(), 0);
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) return;
    setIsVerifying(true);
    try {
      await dispatch(verifyEmailOtp({ email: emailFromState, otp })).unwrap();
      toast.success("User registered successfully")
      navigate("/login", { replace: true });
    } catch (err) {
      // show minimal inline error feedback by shaking inputs; for now, just clear OTP
      setOtpArr(Array(6).fill(""));
      inputsRef.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setResendTimer(60);
    try {
      await dispatch(resendEmailOtp({ email: emailFromState })).unwrap();
      toast.success("Verification code resent successfully");
    } catch (err) {
      // ignore; timer still runs
    } finally {
      setIsResending(false);
    }

    // Start countdown
    if (resendIntervalRef.current) {
      clearInterval(resendIntervalRef.current);
    }
    resendIntervalRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(resendIntervalRef.current);
          resendIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const maskedEmail = (emailFromState || "").replace(/(.{2})(.*)(@.*)/, "$1***$3");

  return (
    <div className="relative z-10 flex min-h-[100dvh] items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <Button
          ghost
          onClick={() => navigate("/signup", { replace: true })}
          className="mb-8 text-gray-400 hover:text-white hover:bg-slate-800/50 border border-slate-700/50 backdrop-blur-sm flex items-center"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Sign Up
        </Button>

        {/* Main Card */}
        <div className="bg-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 shadow-2xl relative overflow-hidden">
          {/* Glow Effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-cyan-500/5 rounded-2xl" />
          <div className="absolute -top-1 -left-1 -right-1 -bottom-1 bg-gradient-to-r from-blue-500/20 via-cyan-500/20 to-blue-500/20 rounded-2xl blur-sm -z-10" />

          <div className="relative">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/25">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-white mb-2">Verify Your Email</h1>
              <p className="text-gray-400">We've sent a 6-digit verification code to</p>
              <p className="text-blue-400 mt-1">{maskedEmail}</p>
            </div>

            {/* OTP Input */}
            <div className="mb-6">
              <label className="block text-gray-300 mb-4 text-center">
                Enter verification code
              </label>
              <div className="flex justify-center gap-3">
                {otpArr.map((digit, i) => (
                  <Input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el?.input ?? el)} // ant input exposes .input
                    value={digit}
                    onChange={(e) => handleChange(e.target.value, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    onPaste={handlePaste}
                    maxLength={1}
                    size="large"
                    inputMode="numeric"
                    className="w-12 h-12 text-center text-white text-lg rounded-lg bg-slate-800/50 border-2 border-slate-600 focus:border-blue-500 focus:bg-slate-700/50 transition-all duration-200"
                  />
                ))}
              </div>
            </div>

            {/* Verify Button */}
            <Button
              onClick={handleVerifyOTP}
              disabled={otp.length !== 6 || isVerifying}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white border-0 h-12 rounded-lg shadow-lg shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isVerifying ? (
                <span className="inline-flex items-center">
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </span>
              ) : (
                "Verify Email"
              )}
            </Button>

            {/* Resend Code */}
            <div className="mt-6 text-center">
              <p className="text-gray-400 mb-2">Didn't receive the code?</p>
              <Button
                ghost
                onClick={handleResendCode}
                disabled={isResending || resendTimer > 0}
                className="text-blue-400 hover:text-blue-300 hover:bg-slate-700/50 disabled:!text-white"
              >
                {isResending ? (
                  <span className="inline-flex items-center text-white">
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </span>
                ) : resendTimer > 0 ? (
                  `Resend in ${resendTimer}s`
                ) : (
                  "Resend Code"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
